import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { Filters, FiltersContextValue, FiltersProviderProps } from './FiltersContext.types'
import { useSearchParams } from 'react-router-dom'
import FiltersList from './FiltersList'
import { generateQueryParams, readQueryParams } from 'utils/generateQueryParams'
import { renderDate } from 'utils/renderDate'
import { ExportForm } from './partials/export-form'
import { Box, Typography } from '@mui/material'
// import { useTranslation } from 'react-i18next'

const defaultValue: FiltersContextValue = {
  filters: {},
  updateFilter: () => {},
  changeFilters: () => {},
  toggleDrawer: () => {},
  isOpen: false,
  options: {},
  headings: [],
  customFilters: [],
  isExportMode: false,
  exportText: '',
  setResultsCount: () => {},
}

export const FiltersContext = createContext(defaultValue)

export const useFilters = (): FiltersContextValue => {
  const data = useContext(FiltersContext)

  return data
}

const FiltersProvider = ({
  children,
  options,
  headings,
  isExportMode,
  exportText,
  disableFilters,
  onUsedFilters,
  changeExportMode,
  customFilters,
  renderFilters,
  onExport,
  columnsToExport,
  filterGroups,
  disableSearchParams = false,
}: FiltersProviderProps): JSX.Element => {
  const loadedParams = useRef(false)
  const [isOpen, setIsOpen] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const [filters, setFilters] = useState<Filters>({})
  const [resultsCount, setResultsCount] = useState<number | null>(null)

  const updateFilter = (newFilters: Filters): void => {
    setFilters(prev => ({ ...prev, ...newFilters }))
    setIsOpen(false)
  }

  const changeFilters = (newFilters: Filters): void => {
    const newData = Object.fromEntries(
      Object.entries(newFilters).map(([key, value]) => [
        key,
        value instanceof Array ? value.map(e => e.trim()) : value.toString().trim(),
      ])
    )
    setFilters(prev => ({
      page: prev.page,
      order: prev.order,
      orderField: prev.orderField,
      ...newData,
    }))
    setIsOpen(false)
  }

  useEffect(() => {
    if (loadedParams.current) {
      const {
        createdAtStart,
        createdAtFinish,
        createdAtLocalStart,
        createdAtLocalFinish,
        verifiedAtLocalStart,
        verifiedAtLocalFinish,
        ...restFilters
      } = filters
      const values = generateQueryParams(restFilters as Record<string, string | string[]>)
      if (!disableSearchParams) {
        setSearchParams(prev => ({ ...prev, ...values }))
      }

      onUsedFilters?.({
        ...restFilters,
        createdAtStart: createdAtStart ? renderDate(createdAtStart.toString(), true, '-', true) : '',
        createdAtFinish: createdAtFinish ? renderDate(createdAtFinish.toString(), true, '-', true) : '',
        createdAtLocalStart: createdAtLocalStart ? renderDate(createdAtLocalStart.toString(), true, '-', true) : '',
        createdAtLocalFinish: createdAtLocalFinish ? renderDate(createdAtLocalFinish.toString(), true, '-', true) : '',
        verifiedAtLocalStart: verifiedAtLocalStart ? renderDate(verifiedAtLocalStart.toString(), true, '-', true) : '',
        verifiedAtLocalFinish: verifiedAtLocalFinish
          ? renderDate(verifiedAtLocalFinish.toString(), true, '-', true)
          : '',
      })
    }
  }, [filters])

  useEffect(() => {
    const data = readQueryParams(searchParams)
    changeFilters(data)
    loadedParams.current = true
  }, [])

  const toggleDrawer = (newValue?: boolean): void => {
    if (newValue !== undefined) {
      setIsOpen(newValue)
    } else {
      setIsOpen(p => !p)
    }
  }

  const filtersView = useMemo(
    () => (!disableFilters ? <FiltersList filterGroups={filterGroups} /> : null),
    [filters, filterGroups]
  )

  return (
    <FiltersContext.Provider
      value={{
        filters,
        updateFilter,
        changeFilters,
        toggleDrawer,
        isOpen,
        options,
        customFilters,
        headings,
        isExportMode,
        exportText,
        changeExportMode,
        renderFilters,
        onExport,
        setResultsCount,
      }}
    >
      <Box display="flex" justifyContent="space-between" gap={2}>
        {filtersView}
        {resultsCount !== null && (
          <Typography mt={0.5}>
            Found <b>{resultsCount}</b> entries
          </Typography>
        )}
      </Box>
      {children}
      <ExportForm columnsToExport={columnsToExport} />
    </FiltersContext.Provider>
  )
}

export default FiltersProvider
